import { cssVariables } from '@/cssVariables'

const { breakpoints } = cssVariables

export type BreakpointKey = keyof typeof breakpoints

/**
 * How wide the image is at a given breakpoint:
 *   "n/d"  — fraction of wrapper width, e.g. "1/2", "2/3", "1/3"
 *   "n%"   — percentage of wrapper width, e.g. "50%", "75%"
 *   "n"    — absolute pixel value, e.g. "188", "640"
 */
type SizeInput = `${number}/${number}` | `${number}%` | `${number}`

/**
 * Returns the fraction (0–1) for fraction/percentage inputs, or null for absolute values.
 * Absolute values are viewport-independent and bypass wrapper scaling entirely.
 */
function getFraction(input: SizeInput): number | null {
  if (input.includes('/')) {
    const [num, den] = input.split('/').map(Number)
    return num! / den!
  }
  if (input.includes('%')) {
    return parseFloat(input) / 100
  }
  return null
}

/**
 * Generates a `sizes` string for `next/image` based on breakpoint/size pairs.
 *
 * Below the smallest specified breakpoint the image is assumed to be full-width (100vw).
 * Fraction-based sizes below the wrapper breakpoint resolve to `vw` units so the browser
 * tracks the viewport width accurately — switching to a fixed `px` value only once the
 * viewport exceeds the wrapper cap. Absolute pixel values are always fixed regardless of
 * breakpoint. Pairs are sorted automatically so order doesn't matter.
 *
 * Do not use this utility for full-width images. Use `100vw` instead.
 * Do not use this utility for fixed-width images. Use `generateFixedImageSizes`
 * or a raw `{width}px` string instead.
 *
 * @example
 * Full-width on mobile, half-width above lg, capped at wrapper
 * generateImageSizes([['lg', '1/2']])
 * → "(max-width: 1024px) 100vw, (max-width: 1360px) 50vw, 680px"
 *
 * @example
 * Full-width on mobile, 2/3 the wrapper on sm
 * generateImageSizes([['sm', '2/3']])
 * → "(max-width: 640px) 100vw, (max-width: 1360px) 67vw, 907px"
 *
 * @example
 * Full-width on mobile, half-width at md, a third at lg, capped at wrapper
 * generateImageSizes([['md', '1/2'], ['lg', '1/3']])
 * → "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, (max-width: 1360px) 33vw, 453px"
 *
 * @example
 * Full-width on mobile, fixed 188px above the lg breakpoint
 * generateImageSizes([['lg', '188']])
 * → "(max-width: 1024px) 100vw, 188px"
 */
export function generateImageSizes(entries: [BreakpointKey, SizeInput][]): string {
  if (entries.length === 0) {
    return `(max-width: ${breakpoints.wrapper}px) 100vw, ${breakpoints.wrapper}px`
  }

  // Always sort ascending by breakpoint value so callers don't have to
  const sorted = [...entries].sort(([a], [b]) => breakpoints[a] - breakpoints[b])

  const parts: string[] = []

  // Below the smallest breakpoint: always full viewport width
  const [firstBp] = sorted[0]!
  parts.push(`(max-width: ${breakpoints[firstBp]}px) 100vw`)

  sorted.forEach(([bp, size], i) => {
    const isLast = i === sorted.length - 1
    const fraction = getFraction(size)
    const isAbsolute = fraction === null
    const belowWrapper = breakpoints[bp] < breakpoints.wrapper

    if (isAbsolute) {
      // Fixed pixel value — always the same regardless of viewport or breakpoint
      const px = Math.round(parseFloat(size))
      parts.push(isLast ? `${px}px` : `(max-width: ${breakpoints[sorted[i + 1]![0]]}px) ${px}px`)
    } else if (belowWrapper) {
      // Fraction below the wrapper — image tracks viewport width until the wrapper caps it.
      // Use vw units here so the browser picks the right srcset candidate at each viewport size.
      const vw = Math.round(fraction * 100)
      if (isLast) {
        // Inject the wrapper cap before the final fixed value
        parts.push(`(max-width: ${breakpoints.wrapper}px) ${vw}vw`)
        parts.push(`${Math.round(breakpoints.wrapper * fraction)}px`)
      } else {
        parts.push(`(max-width: ${breakpoints[sorted[i + 1]![0]]}px) ${vw}vw`)
      }
    } else {
      // Fraction at or above the wrapper — container is always wrapper-width, use fixed px
      const px = Math.round(breakpoints.wrapper * fraction)
      parts.push(isLast ? `${px}px` : `(max-width: ${breakpoints[sorted[i + 1]![0]]}px) ${px}px`)
    }
  })

  return parts.join(', ')
}

/** Absolute pixel width for fixed decorative image slots. */
type FixedPxInput = number | `${number}`

export type FixedSizeEntry = ['default', FixedPxInput] | [BreakpointKey, FixedPxInput]

function parseFixedPx(size: FixedPxInput): number {
  return Math.round(typeof size === 'number' ? size : parseFloat(size))
}

/**
 * Generates a `sizes` string for fixed-width decorative images that change at
 * Tailwind breakpoints (e.g. `w-[380px] lg:w-[450px] xl:w-[570px]`).
 *
 * Unlike `generateImageSizes`, this never injects `100vw` — the slot width is
 * fixed at every viewport. Use a `'default'` entry for the base size below the
 * first breakpoint; it is required when more than one entry is provided.
 *
 * For a single fixed width at all viewports, pass one entry or use `"400px"`.
 *
 * @example
 * generateFixedImageSizes([
 *   ['default', '380'],
 *   ['lg', '450'],
 *   ['xl', '570'],
 * ])
 * → "(max-width: 1024px) 380px, (max-width: 1280px) 450px, 570px"
 *
 * @example
 * generateFixedImageSizes([['default', '315']])
 * → "315px"
 */
export function generateFixedImageSizes(entries: FixedSizeEntry[]): string {
  if (entries.length === 0) {
    throw new Error('generateFixedImageSizes requires at least one entry')
  }

  const defaultEntry = entries.find(([bp]) => bp === 'default')
  const breakpointEntries = entries
    .filter((entry): entry is [BreakpointKey, FixedPxInput] => entry[0] !== 'default')
    .sort(([a], [b]) => breakpoints[a] - breakpoints[b])

  if (entries.length > 1 && !defaultEntry) {
    throw new Error(
      'generateFixedImageSizes requires a ["default", "{px}"] entry when multiple sizes are provided',
    )
  }

  if (breakpointEntries.length === 0) {
    if (!defaultEntry) {
      throw new Error(
        'generateFixedImageSizes requires a ["default", "{px}"] entry for a single fixed width',
      )
    }
    return `${parseFixedPx(defaultEntry[1])}px`
  }

  const basePx = parseFixedPx((defaultEntry ?? breakpointEntries[0]!)[1])
  const parts: string[] = [`(max-width: ${breakpoints[breakpointEntries[0]![0]]}px) ${basePx}px`]

  breakpointEntries.forEach(([, size], i) => {
    const px = parseFixedPx(size)
    const isLast = i === breakpointEntries.length - 1
    parts.push(
      isLast ? `${px}px` : `(max-width: ${breakpoints[breakpointEntries[i + 1]![0]]}px) ${px}px`,
    )
  })

  return parts.join(', ')
}
